/**
 * @file Renders the editable note form with its last-updated stamp.
 */
import React, { useMemo } from "react";

import { Form } from "@components/Form";
import { W0, W1 } from "@components/Constants";
import { useAppContext } from "@components/AppContext";
import type { Operation } from "@api";
import type { AppContext } from "@components/AppContext/types";

import Footer from "./Footer";

const LABEL_SUBMIT = "Save";
const LABEL_SUCCESS = "Note is saved";

const NOTE_ROWS = 4;
const NOTE_MAX_LENGTH = 2048;

const NOTE_ITEM_STYLE = { paddingBottom: 0, marginBottom: 0 };

const SUBMIT_ITEM_PROPS = {
  style: {
    marginTop: -1 * W1,
    marginBottom: W0,
  },
};

const SUBMIT_BUTTON_PROPS = {
  size: "small",
  style: { width: "initial" },
};

type Props = {
  id: string;
  note?: string;
  operation: Operation;
  updatedAt?: string;
  updatedBy?: string;
  isEditable: boolean;
  onAfterSubmit?: (updatedItem?: unknown) => void;
};

/**
 * Renders the editable note form with its last-updated stamp.
 */
const Note = ({
  note = "",
  updatedAt,
  updatedBy,
  onAfterSubmit,
  id,
  operation,
  isEditable,
}: Props): JSX.Element => {
  const item = useMemo(() => ({ id, note }), [ id, note ]);

  const { request, showSuccessMessage } = useAppContext() as AppContext;

  /**
   * Renders the note's last-updated stamp field.
   */
  const renderStamps = (): JSX.Element => (
    <Footer
      updatedAt={updatedAt}
      updatedBy={updatedBy}
    />
  );

  const schema = operation.getSchema([
    {
      name: "note",
      label: "",
      itemProps: {
        style: NOTE_ITEM_STYLE,
      },
      inputProps: {
        rows: NOTE_ROWS,
        disabled: !isEditable,
        showCount: true,
        maxLength: NOTE_MAX_LENGTH,
      },
    },
    {
      name: "_stamps",
      component: renderStamps,
    },
  ]);

  // The Form component infers the callback without a parameter from its
  // empty default, so the parameter is optional.
  /**
   * Runs the given after-submit callback, then shows the save success message.
   */
  const onSuccess = (updatedItem?: unknown): void => {
    const hasAfterSubmit = !!onAfterSubmit;

    if (hasAfterSubmit) {
      onAfterSubmit(updatedItem);
    }

    showSuccessMessage(LABEL_SUCCESS);
  };

  const submitLabel = LABEL_SUBMIT;
  const hasSubmitButton = isEditable;

  const formProps = {
    item,
    schema,
    request,
    onSuccess,
    operation,
    submitLabel,
    hasSubmitButton,
    submitItemProps: SUBMIT_ITEM_PROPS,
    submitButtonProps: SUBMIT_BUTTON_PROPS,
  };

  return (
    <Form {...formProps} />
  );
};

export default Note;
